import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { OrganisationSelector } from '@/components/shared/OrganisationSelector';
import { useToast } from '@/hooks/use-toast';
import { 
  FileText, 
  Video, 
  BookOpen, 
  CheckCircle, 
  Shield, 
  Award,
  Star,
  AlertTriangle
} from 'lucide-react';

const DITAN_LOGO = "/images/DitanLogo.png";

const features = [
  { 
    icon: FileText, 
    title: "Expert-Curated Policies", 
    desc: "Ready-to-use policy documents drafted by qualified SA labour attorneys" 
  },
  { 
    icon: Video, 
    title: "Training Videos", 
    desc: "Bite-sized video modules explaining key compliance topics" 
  },
  { 
    icon: BookOpen, 
    title: "Digital Flashcards", 
    desc: "Reinforce learning with swipeable study cards" 
  },
  { 
    icon: CheckCircle, 
    title: "Assessments", 
    desc: "100% pass requirement proves actual comprehension" 
  },
  { 
    icon: Shield, 
    title: "Identity Verification", 
    desc: "Selfie + ID capture creates legally defensible records" 
  },
  { 
    icon: Award, 
    title: "Certificates", 
    desc: "Downloadable proof of completion for each employee" 
  },
];

const steps = [
  "We set up your branded portal",
  "Employees complete training modules",
  "System captures proof of understanding",
  "You have audit-ready evidence"
];

export default function Marketing() {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });

  const scrollTo = (id: string) => {
    const element = document.querySelector(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    toast({
      title: "Message sent!",
      description: "We'll get back to you within 24 hours.",
    });
    setFormData({ name: '', email: '', company: '', message: '' });
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-background/95 backdrop-blur border-b border-border">
        <nav className="container flex items-center justify-between py-4">
          <div className="relative rounded-lg p-[3px] bg-[length:400%_100%] bg-gradient-to-r from-[#C27C4F] via-[#E5C89C] to-[#C27C4F] shadow-[0_0_20px_rgba(194,124,79,0.5)] animate-shimmer">
            <div className="bg-[#FFFBF7] rounded-[5px] p-2 shadow-[inset_0_1px_3px_rgba(0,0,0,0.1)]">
              <img src={DITAN_LOGO} alt="Ditan" className="h-8" loading="lazy" />
            </div>
          </div>
          <div className="hidden md:flex items-center gap-6">
            <button 
              onClick={() => scrollTo('#features')} 
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              Features
            </button>
            <button 
              onClick={() => scrollTo('#pricing')} 
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              Pricing
            </button>
            <button 
              onClick={() => scrollTo('#contact')} 
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              Contact
            </button>
          </div>
          <div className="relative rounded-md p-[2px] bg-[length:400%_100%] bg-gradient-to-r from-[#C27C4F] via-[#E5C89C] to-[#C27C4F] animate-shimmer">
            <Button onClick={() => scrollTo('#contact')} className="bg-background hover:bg-background/90 text-foreground rounded-[4px]">Get Started</Button>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="flex flex-col justify-start pt-28 sm:pt-32 pb-16 sm:pb-20 px-4">
        <div className="container text-center max-w-4xl">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold mb-6">
            We Simplify Labour Law Compliance for Your Business
          </h1>
          <p className="text-lg sm:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Expert-curated training that proves your employees understand company policies — 
            creating audit-ready evidence for CCMA proceedings.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <div className="relative rounded-md p-[2px] bg-[length:400%_100%] bg-gradient-to-r from-[#C27C4F] via-[#E5C89C] to-[#C27C4F] animate-shimmer w-fit">
              <Button size="lg" onClick={() => scrollTo('#contact')} className="bg-background hover:bg-background/90 text-foreground rounded-[4px]">
                Get Started
              </Button>
            </div>
            <OrganisationSelector variant="button" />
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-12 sm:py-16 bg-secondary/30">
        <div className="container text-center max-w-3xl px-4">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold mb-4">
            The R2 Billion Problem
          </h2>
          <p className="text-lg text-muted-foreground">
            South African businesses lose labour disputes not because they lack policies, 
            but because they can't prove employees understood them. 
            A signature on a form proves nothing.
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 sm:py-20 scroll-mt-20">
        <div className="container px-4">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-center mb-10">
            What We Provide
          </h2>
          
          {/* KEY FEATURE Hero Card */}
          <Card className="bg-card border-border mb-8">
            <CardContent className="p-6 sm:p-8">
              <div className="flex flex-col sm:flex-row items-start gap-4">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                  <Star className="h-7 w-7 text-yellow-500 fill-yellow-500" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-xl sm:text-2xl mb-2">
                    Audit-Ready Compliance Records
                  </h3>
                  <p className="text-muted-foreground">
                    Every training completion creates an immutable record with ID verification, 
                    selfie, timestamp, and policy acknowledgment — exactly what you need for CCMA proceedings.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature) => {
              const IconComponent = feature.icon;
              return (
                <Card key={feature.title} className="bg-card border-border">
                  <CardContent className="p-6">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 mb-4">
                      <IconComponent className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="font-heading font-semibold text-lg mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground">{feature.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-12 sm:py-16 bg-secondary/30">
        <div className="container px-4">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-center mb-8">
            How It Works
          </h2>
          <div className="flex flex-col md:flex-row gap-6 md:gap-4 justify-center items-start md:items-start max-w-4xl mx-auto">
            {steps.map((step, i) => (
              <div key={i} className="flex md:flex-col items-center gap-4 text-left md:text-center flex-1 w-full md:w-auto">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground font-bold text-lg">
                  {i + 1}
                </span>
                <span className="text-foreground md:mt-2">{step}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROI Section */}
      <section id="pricing" className="py-16 sm:py-20 scroll-mt-20">
        <div className="container max-w-4xl px-4">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-center mb-3">
            One Avoided Dispute Pays for Years
          </h2>
          <p className="text-muted-foreground text-center mb-8">Do the maths...</p>
          
          <div className="grid md:grid-cols-2 gap-6 items-stretch">
            {/* Risk Card */}
            <div className="relative rounded-2xl border-2 border-destructive/30 bg-destructive/5 p-6 text-center">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-destructive text-destructive-foreground text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wide">
                The Risk
              </div>
              <div className="flex justify-center mb-4 mt-2">
                <AlertTriangle className="h-10 w-10 text-destructive" />
              </div>
              <p className="text-sm text-muted-foreground mb-2">Average unfair dismissal award</p>
              <p className="text-4xl font-bold text-destructive">R150,000+</p>
            </div>

            {/* Solution Card */}
            <div className="relative rounded-2xl border-2 border-primary/30 bg-primary/5 p-6 text-center">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wide">
                The Solution
              </div>
              <div className="flex justify-center mb-4 mt-2">
                <Shield className="h-10 w-10 text-primary" />
              </div>
              <p className="text-sm text-muted-foreground mb-2">Ditan Compliance Platform</p>
              <p className="text-4xl font-bold text-primary">R50</p>
              <p className="text-sm text-muted-foreground">/employee/month</p>
              <p className="text-xs text-muted-foreground mt-2">(Setup Fees Apply)</p>
            </div>
          </div>
        </div>
      </section>

      {/* Organisation Selector Section */}
      <section className="py-12 sm:py-16 bg-secondary/30">
        <div className="container text-center max-w-md px-4">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold mb-4">
            Already Have an Account?
          </h2>
          <OrganisationSelector variant="standalone" />
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 sm:py-20 scroll-mt-20">
        <div className="container max-w-lg px-4">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-center mb-6">
            Ready to Protect Your Business?
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input 
              placeholder="Name" 
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              required
              className="bg-card border-border"
            />
            <Input 
              placeholder="Email" 
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              required
              className="bg-card border-border"
            />
            <Input 
              placeholder="Company" 
              value={formData.company}
              onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
              required
              className="bg-card border-border"
            />
            <Textarea 
              placeholder="Message" 
              value={formData.message}
              onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
              rows={4}
              className="bg-card border-border"
            />
            <div className="relative rounded-md p-[2px] bg-[length:400%_100%] bg-gradient-to-r from-[#C27C4F] via-[#E5C89C] to-[#C27C4F] shadow-[0_0_12px_rgba(194,124,79,0.4)] animate-shimmer">
              <Button type="submit" className="w-full bg-background hover:bg-background/90 text-foreground rounded-[4px]">
                Request Demo
              </Button>
            </div>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-border">
        <div className="container flex flex-col md:flex-row items-center justify-between gap-4 px-4">
          <p className="text-muted-foreground text-sm">
            © 2025 Ditan Attorneys. All rights reserved.
          </p>
          <div className="flex gap-4 text-sm text-muted-foreground">
            <a href="#" className="hover:text-foreground transition-colors">Privacy Policy</a>
            <a href="#" className="hover:text-foreground transition-colors">Terms</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
